import { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  KeyRound,
  LoaderCircle,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { authClient } from "../lib/auth-client";
import { breadcrumbOtpSend, reportOtpSendError } from "../lib/otp-telemetry";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { Label } from "./ui/label";
import { PhoneInput } from "./ui/phone-input";

const logoLight = "/branding/logo-light.svg";

type Step = "name" | "phone" | "email" | "passkey" | "done";

type ProfileData = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  emailVerified: boolean;
  image: string | null;
};

type StepMeta = {
  id: Exclude<Step, "done">;
  icon: React.ElementType;
  label: string;
};

const STEPS: StepMeta[] = [
  { id: "name", icon: User, label: "Navn" },
  { id: "phone", icon: Phone, label: "Telefon" },
  { id: "email", icon: Mail, label: "E-post" },
  { id: "passkey", icon: KeyRound, label: "Passkey" },
];

function buildStepQueue(profile: ProfileData, passkeyCount: number): Step[] {
  const queue: Step[] = [];
  if (!profile.firstName) queue.push("name");
  if (!profile.phoneNumber) queue.push("phone");
  if (!profile.emailVerified) queue.push("email");
  if (passkeyCount === 0) queue.push("passkey");
  queue.push("done");
  return queue;
}

export function ProfileSetup({
  profile,
  passkeyCount,
  onProfileRefresh,
  onDefer,
  onComplete,
}: {
  profile: ProfileData;
  passkeyCount: number;
  onProfileRefresh: () => Promise<void>;
  onDefer: () => void;
  onComplete: () => void;
}) {
  const [stepQueue] = useState<Step[]>(() => buildStepQueue(profile, passkeyCount));
  const [stepIndex, setStepIndex] = useState(0);

  const currentStep = stepQueue[stepIndex] ?? "done";
  const visibleSteps = STEPS.filter((step) => stepQueue.includes(step.id));
  const progressPercent =
    stepQueue.length > 1 ? Math.round((stepIndex / (stepQueue.length - 1)) * 100) : 100;

  const advance = async () => {
    await onProfileRefresh();
    setStepIndex((current) => current + 1);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="hidden border-r border-border bg-muted/40 p-8 lg:flex lg:w-80 xl:w-96 xl:flex-col xl:justify-between">
        <div className="flex items-center gap-2.5">
          <img src={logoLight} alt="Forhandlerbørsen" className="h-11 w-auto" />
        </div>

        <div className="space-y-1">
          {visibleSteps.map((step) => {
            const idx = stepQueue.indexOf(step.id);
            const isDone = idx !== -1 && stepIndex > idx;
            const isActive = step.id === currentStep;
            const Icon = step.icon;

            return (
              <div
                key={step.id}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive
                    ? "bg-primary/10 font-medium text-primary"
                    : isDone
                      ? "text-muted-foreground"
                      : "text-muted-foreground/50"
                  }`}
              >
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${isDone ? "bg-green-100" : isActive ? "bg-primary/20" : "bg-muted"
                    }`}
                >
                  {isDone ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Icon className={`h-3.5 w-3.5 ${isActive ? "text-primary" : ""}`} />
                  )}
                </div>
                {step.label}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground">
          Du kan endre dette senere i profilinnstillingene.
        </p>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md space-y-8">
          {currentStep !== "done" ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Oppsett av profil</span>
                <span className="text-muted-foreground">{progressPercent}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          ) : null}

          <div className="glass rounded-[2rem] border border-white/50 p-6">
            {currentStep === "name" ? <NameStep onComplete={advance} /> : null}
            {currentStep === "phone" ? (
              <PhoneStep onComplete={advance} onSkip={advance} currentPhone={profile.phoneNumber} />
            ) : null}
            {currentStep === "email" ? (
              <EmailStep
                onComplete={advance}
                onSkip={advance}
                currentEmail={profile.email}
              />
            ) : null}
            {currentStep === "passkey" ? (
              <PasskeyStep onComplete={advance} onSkip={advance} />
            ) : null}
            {currentStep === "done" ? <DoneStep onDefer={onDefer} onFinish={onComplete} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function NameStep({ onComplete }: { onComplete: () => void }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      setError("Vennligst fyll inn både fornavn og etternavn.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:9000"}/api/users/profile`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Vi klarte ikke å lagre navnet ditt.");
      }

      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Header
        title="Hva heter du?"
        description="Ditt for- og etternavn vises til andre brukere i organisasjonen."
      />
      {error ? <InlineError message={error} /> : null}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="firstName">Fornavn</Label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            placeholder="Ola"
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lastName">Etternavn</Label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            placeholder="Nordmann"
          />
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
        Fortsett
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
}

function PhoneStep({
  onComplete,
  onSkip,
  currentPhone,
}: {
  onComplete: () => void;
  onSkip: () => void;
  currentPhone?: string | null;
}) {
  const [phone, setPhone] = useState(currentPhone ?? "");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSendOtp = async () => {
    setLoading(true);
    setError("");
    const normalizedPhone = phone.trim();
    const otpContext = { screen: "profile-setup" as const, isResend: otpSent, phone: normalizedPhone };
    try {
      breadcrumbOtpSend(otpContext);
      const { error } = await authClient.phoneNumber.sendOtp({
        phoneNumber: normalizedPhone,
      });
      if (error) {
        reportOtpSendError(error, otpContext);
        setError("Vi klarte ikke å sende SMS-koden.");
        return;
      }
      setOtpSent(true);
    } catch (err) {
      reportOtpSendError(err, otpContext);
      setError(err instanceof Error ? err.message : "Noe gikk galt.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError("");
    try {
      const { error } = await (authClient.phoneNumber as any).verify({
        phoneNumber: phone.trim(),
        code: otp,
        updatePhoneNumber: true,
      });
      if (error) throw new Error("Koden stemte ikke. Prøv igjen.");
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Header
        title="Legg til mobilnummer"
        description="Mobilnummeret brukes til sikre innlogginger med SMS-kode."
      />
      {error ? <InlineError message={error} /> : null}
      {!otpSent ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="setupPhone">Mobilnummer</Label>
            <PhoneInput
              id="setupPhone"
              value={phone as any}
              onChange={(value: string | undefined) => setPhone(value ?? "")}
              defaultCountry="NO"
              international={false}
              countryCallingCodeEditable={false}
              autoFocus
            />
          </div>
          <Button onClick={handleSendOtp} className="w-full" disabled={loading || !phone.trim()}>
            {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            Send kode
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Skriv inn koden du mottok</Label>
            <InputOTP maxLength={6} value={otp} onChange={setOtp}>
              <InputOTPGroup className="gap-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button onClick={handleVerify} className="w-full" disabled={loading || otp.length < 6}>
            {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            Bekreft
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <button
            type="button"
            onClick={() => {
              setOtpSent(false);
              setOtp("");
            }}
            className="w-full text-center text-xs text-muted-foreground underline"
          >
            Send ny kode
          </button>
        </div>
      )}
      <SkipButton onSkip={onSkip} />
    </div>
  );
}

function EmailStep({
  onComplete,
  onSkip,
  currentEmail,
}: {
  onComplete: () => void;
  onSkip: () => void;
  currentEmail?: string | null;
}) {
  const [email, setEmail] = useState(currentEmail || "");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSendOtp = async () => {
    setLoading(true);
    setError("");
    try {
      const { error } = await authClient.emailOtp.sendVerificationOtp({
        email: email.trim(),
        type: "email-verification",
      });
      if (error) throw new Error("Vi klarte ikke å sende koden til e-postadressen din.");
      setOtpSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError("");
    try {
      const { error } = await (authClient.emailOtp as any).verifyEmail({
        email: email.trim(),
        otp,
      });
      if (error) throw new Error("Koden stemte ikke. Prøv igjen.");
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Header
        title="Bekreft e-postadresse"
        description="Vi sender en kode til e-postadressen din for å bekrefte den."
      />
      {error ? <InlineError message={error} /> : null}
      {!otpSent ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="setupEmail">E-postadresse</Label>
            <Input
              id="setupEmail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="deg@eksempel.no"
              autoFocus
            />
          </div>
          <Button onClick={handleSendOtp} className="w-full" disabled={loading || !email.trim()}>
            {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            Send kode
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Skriv inn koden du mottok</Label>
            <InputOTP maxLength={6} value={otp} onChange={setOtp}>
              <InputOTPGroup className="gap-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button onClick={handleVerify} className="w-full" disabled={loading || otp.length < 6}>
            {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            Bekreft
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <button
            type="button"
            onClick={() => {
              setOtpSent(false);
              setOtp("");
            }}
            className="w-full text-center text-xs text-muted-foreground underline"
          >
            Send ny kode
          </button>
        </div>
      )}
      <SkipButton onSkip={onSkip} />
    </div>
  );
}

function PasskeyStep({
  onComplete,
  onSkip,
}: {
  onComplete: () => void;
  onSkip: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    setLoading(true);
    setError("");
    try {
      const { error } = await authClient.passkey.addPasskey();
      if (error) throw new Error(error.message);
      onComplete();
    } catch (err: any) {
      if (err?.name === "NotAllowedError" || err?.message?.includes("cancelled")) {
        setError("Opprettelsen ble avbrutt. Du kan prøve igjen eller hoppe over.");
      } else {
        setError("Vi klarte ikke å opprette passkey akkurat nå.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Header
        title="Sett opp passkey"
        description="Logg inn uten passord med Touch ID, Face ID eller sikkerhetsnøkkel."
      />
      {error ? <InlineError message={error} /> : null}
      <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-4">
        <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="space-y-1 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Hva er en passkey?</p>
          <p>En passkey lagres på enheten din og lar deg logge inn med biometri eller PIN.</p>
        </div>
      </div>
      <Button onClick={handleCreate} className="w-full" disabled={loading}>
        {loading ? (
          <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <KeyRound className="mr-2 h-4 w-4" />
        )}
        Opprett passkey
      </Button>
      <SkipButton onSkip={onSkip} />
    </div>
  );
}

function DoneStep({
  onDefer,
  onFinish,
}: {
  onDefer: () => void;
  onFinish: () => void;
}) {
  return (
    <div className="space-y-6 text-center">
      <div className="flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
      </div>
      <Header
        title="Alt er klart"
        description="Profilen din er satt opp. Du er klar til å gå videre i plattformen."
      />
      <Button
        className="w-full"
        onClick={() => {
          onDefer();
          onFinish();
        }}
      >
        Gå videre
        <ChevronRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function Header({ title, description }: { title: string; description: string }) {
  return (
    <div className="space-y-1">
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{message}</p>
  );
}

function SkipButton({ onSkip }: { onSkip: () => void }) {
  return (
    <button
      type="button"
      onClick={onSkip}
      className="w-full text-center text-sm text-muted-foreground underline underline-offset-2"
    >
      Hopp over for nå
    </button>
  );
}
